import React, {useEffect, useReducer, useRef} from 'react';
import {Platform, StyleProp, StyleSheet, View, ViewStyle} from 'react-native';

import {logger} from '../../../utils/logger';
import TextInput from '../../themed/TextInput';
import {fontFamilyStyle} from '../Text';

type State = {cursor: number; values: string[]};

type Action =
  | {type: 'reset'}
  | {type: 'insertText'; payload: {value: string; index: number}}
  | {type: 'insertFromPaste'; payload: {value: string}}
  | {type: 'sms'; payload: {value: string}}
  | {type: 'deleteContentBackward'; payload: {index: number}};

function reducer(state: State, action: Action) {
  if (action.type === 'reset') {
    return {cursor: 0, values: Array(state.values.length).fill('')};
  }

  // Create new values array to avoid mutation
  const newValues = [...state.values];
  let newCursor = state.cursor;

  if (action.type === 'insertText') {
    newValues[action.payload.index] = action.payload.value;
  } else if (action.type === 'insertFromPaste') {
    for (let i = 0; i < newValues.length; i++) {
      newValues[i] = action.payload.value[i] || '';
    }
  } else if (action.type === 'deleteContentBackward') {
    newValues[action.payload.index] = '';
  }
  // code from sms safari ios is sent as several
  // onChange event nativeEvent.type = 'input'
  // chrome ios sms sends as 1 value so can use the simpler insertFromPaste
  else if (action.type === 'sms') {
    // value was sometimes 2 char. just the last char should be used.
    newValues[state.cursor] = action.payload.value[action.payload.value.length - 1];
    if (state.cursor === newValues.length - 1) {
      newCursor = 0;
    } else {
      newCursor = state.cursor + 1;
    }
  }

  return {cursor: newCursor, values: newValues};
}

// Both handlers are passed straight through to the TextInput below, so take
// their types from it rather than restating them.
type BoxProps = React.ComponentProps<typeof TextInput>;

export type OtpProps = {
  numInputs: number;
  onChangeText?: (text: string) => void;
  containerStyle?: StyleProp<ViewStyle>;
  onEnter?: () => void;
  onTouchStart?: BoxProps['onTouchStart'];
  onBlur?: BoxProps['onBlur'];

  /**
   * Should apply error styles
   */
  error?: boolean;

  /**
   * This is just used to reset the field when set to empty string
   */
  value?: string;
};

const styles = StyleSheet.create({
  containerStyle: {
    flexDirection: 'row',
    gap: 8,
  },
  textInputStyle: {
    ...fontFamilyStyle,
    textAlign: 'center',
    width: 44,
    paddingHorizontal: 10,
  },
  textInputStyleError: {
    color: '#C81E1E',
  },
  errorStyle: {
    display: 'none',
  },
});

/**
 * Otp for rewards program. evaluated other but they were a bit buggy or didn't have the behaviour we required
 * react-native-otp-textinput react-otp-input
 * @param param0
 * @returns
 */
export default function Otp({
  numInputs = 4,
  onChangeText,
  containerStyle,
  onEnter,
  onTouchStart,
  onBlur,
  error,
  value,
}: OtpProps) {
  const otpInput = useRef(Array(numInputs).fill({}));
  // True only while the reset effect below runs for the first time, whatever
  // `value` was then. See the comment inside that effect.
  const isMountPassRef = useRef(true);

  const [state, dispatch] = useReducer(reducer, {
    // cursor used to keep track of sms input
    cursor: 0,
    values: Array(numInputs).fill(''),
  });

  // Call onChangeText when values change
  const valuesString = state.values.join('');
  useEffect(() => {
    onChangeText?.(valuesString);
  }, [valuesString, onChangeText]);

  // This is a bit of a hack to reset the field
  // if value prop is set to empty string
  useEffect(() => {
    if (value === '') {
      otpInput.current?.forEach(e => {
        e.clear();
      });
      dispatch({type: 'reset'});
      // DIS-1673: an external clear means "start over", so put the caret back
      // in the first box. Without this the caret stays where it was — after a
      // full code that is the LAST box, and the insertText branch below only
      // advances focus while `i < numInputs - 1`, so every retyped digit
      // overwrites that one box and the joined value never reaches numInputs
      // again. On RewardsVerificationScreen, whose auto-submit fires on the
      // joined length and which has no submit button, that is a dead end.
      //
      // Skipped on the mount pass: `value` starts as '' on all three
      // consumers, and focusing there would raise the keyboard on mount — a
      // behaviour change none of them asked for.
      if (!isMountPassRef.current) {
        otpInput.current?.[0]?.focus?.();
      }
    }
    isMountPassRef.current = false;
  }, [value]);

  return (
    <View style={[styles.containerStyle, containerStyle]}>
      {state.values.map((val, i) => {
        return (
          <View key={i} style={{flexGrow: 1, flexShrink: 1}}>
            <TextInput
              autoCapitalize="none"
              keyboardType="numeric"
              largeText
              selectTextOnFocus
              ref={e => (otpInput.current[i] = e)}
              value={val}
              placeholder={'•'}
              pattern="[0-9]"
              onTouchStart={e => {
                onTouchStart?.(e);
              }}
              onBlur={e => {
                onBlur?.(e);
              }}
              onChange={e => {
                logger.debug('Otp', 'Input change event', {inputType: e.nativeEvent.inputType});
                e.preventDefault();
                if (e.nativeEvent.inputType === 'deleteContentBackward') {
                  dispatch({
                    type: 'deleteContentBackward',
                    payload: {index: i},
                  });
                  if (i > 0) {
                    otpInput.current[i - 1].focus();
                  }
                } else if (
                  // web
                  e.nativeEvent.inputType === 'insertText' ||
                  // native
                  Platform.select({native: true})
                ) {
                  // from sms on chrome ios has inputType insertText
                  // also check that data and text match and have 6 chars
                  // to be fairly certain its from sms
                  if (
                    (e.nativeEvent.data === e.nativeEvent.text &&
                      e.nativeEvent.data.length === 6) ||
                    // native
                    (Platform.select({native: true}) && e.nativeEvent.text.length === 6)
                  ) {
                    dispatch({
                      type: 'insertFromPaste',
                      payload: {value: e.nativeEvent.text},
                    });

                    // move focus to the last input and then blur
                    otpInput.current[5].focus();
                    otpInput.current[5].blur();
                  } else {
                    // this is when entering numbers normally web or native
                    dispatch({
                      type: 'insertText',
                      payload: {
                        index: i,
                        value: e.nativeEvent.data || e.nativeEvent.text,
                      },
                    });
                    if (i < numInputs - 1) {
                      otpInput.current[i + 1].focus();
                    }
                  }
                } else if (e.nativeEvent.inputType === 'insertFromPaste') {
                  dispatch({
                    type: 'insertFromPaste',
                    payload: {value: e.nativeEvent.text},
                  });
                }
                // Paste code from SMS ios safari
                // code from sms is sent as several
                // onChange event nativeEvent.type = 'input'
                else if (e.nativeEvent.type === 'input') {
                  dispatch({
                    type: 'sms',
                    payload: {value: e.nativeEvent.text},
                  });
                }
              }}
              onKeyPress={e => {
                if (e.nativeEvent.key === 'Backspace') {
                  if (val.length === 0) {
                    if (i > 0) {
                      e.preventDefault();
                      otpInput.current[i - 1].focus();
                    }
                  }
                } else if (e.nativeEvent.key === 'Enter') {
                  onEnter?.();
                }
              }}
              style={[styles.textInputStyle, error && styles.textInputStyleError]}
              errorStyle={styles.errorStyle}
            />
          </View>
        );
      })}
    </View>
  );
}
